import type { Router } from "vue-router";

const MANIFEST_URL = "/vue/asset-manifest.json";
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const RELOAD_GUARD_KEY = "vc_reloaded_to";

let loadedAppHash: string | null = null;
let reloadPending = false;

// sessionStorage can throw (SecurityError) in privacy modes or sandboxed contexts.
function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage unavailable: the cross-reload guard is skipped, which is safe here.
  }
}

function safeSessionRemove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Storage unavailable: nothing to clear.
  }
}

function extractAppHash(url: string | null | undefined): string | null {
  if (!url) return null;
  const match = url.match(/\/vue\/js\/app\.([^./?]+)\.js/i);
  return match ? match[1] : null;
}

function getLoadedAppHash(): string | null {
  const script = document.querySelector('script[src*="/vue/js/app."]');
  return extractAppHash(script?.getAttribute("src"));
}

async function getDeployedAppHash(): Promise<string | null> {
  const response = await fetch(MANIFEST_URL, { cache: "no-store" });
  if (!response.ok) return null;

  const manifest = (await response.json()) as { js?: string[] };
  const appUrl = (manifest.js ?? []).find((url) => /\/vue\/js\/app\./i.test(url));
  return extractAppHash(appUrl);
}

async function check(immediate: boolean): Promise<void> {
  if (reloadPending || loadedAppHash === null) return;

  let deployedHash: string | null;
  try {
    deployedHash = await getDeployedAppHash();
  } catch {
    return; // network/parse error; the next tick will retry
  }
  if (deployedHash === null) return;

  if (deployedHash === loadedAppHash) {
    // Up to date: clear the loop guard so a future deploy can reload again.
    safeSessionRemove(RELOAD_GUARD_KEY);
    return;
  }

  // A newer build is live. Guard against a reload loop in case a reload does not
  // pick up the new bundle (e.g. an upstream cache still serving the old one).
  if (safeSessionGet(RELOAD_GUARD_KEY) === deployedHash) return;
  safeSessionSet(RELOAD_GUARD_KEY, deployedHash);

  if (immediate) {
    window.location.reload();
  } else {
    // Defer to the next navigation so we never interrupt the current page.
    reloadPending = true;
  }
}

/**
 * Silently keeps the SPA on the latest build, with no UI. A page that booted
 * stale reloads on startup; a build deployed mid-session is picked up on the
 * user's next navigation (full reload to the target route, so a form in progress
 * is never interrupted). Versions are compared via the app.js content hash:
 * the one currently loaded vs the one published in asset-manifest.json.
 */
export function startVersionCheck(router: Router): void {
  loadedAppHash = getLoadedAppHash();

  // Unhashed bundle (e.g. a dev build): cannot compare versions, stay inert.
  if (loadedAppHash === null) return;

  router.beforeEach((to, from) => {
    if (reloadPending && to.fullPath !== from.fullPath) {
      window.location.assign(to.fullPath);
      return false; // cancel the SPA navigation; the full load takes over
    }
    return true;
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void check(false);
  });

  window.setInterval(() => void check(false), POLL_INTERVAL_MS);

  // Reload a page that booted stale (e.g. restored from cache) right away.
  void check(true);
}
